import React, { useState, useEffect } from "react";
import { Court, SportForm, getSportForms } from "api/courts";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, FormControl, InputLabel, Select, MenuItem, TextField, FormControlLabel, Checkbox, InputAdornment, LinearProgress, Tooltip, FormHelperText } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { TimePicker } from "@material-ui/pickers";
import { Info as InfoIcon } from "@material-ui/icons";
import moment from "moment";
import { patchCourts } from "api/admin/courts";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import { Club } from "api/clubs";
import { useSnackbar } from "notistack";
import uI18n from "utils/i18n";
import i18n from "./i18n";

// prop types
interface EditCourtDialogProps {
    club: Club | null,
    court?: Court,
    show: boolean,
    onClose: () => void,
    onRefreshCourtsTable: () => void,
}

// surface types
const surfaceTypes = ["artificial-grass", "asphalt", "carpet", "carpet-fleece", "carpet-with-granulate", "clay", "grass", "hard", "plastic-floor", "sand", "table-tennis-indoor", "table-tennis-stone", "tartan", "woodchip"] as const;

// edit court dialog
const EditCourtDialog = ({ club, court, show, onClose, onRefreshCourtsTable }: EditCourtDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [advanceBookingMins, setAdvanceBookingMins] = useState(0);
    const [bookingPriceCents, setBookingPriceCents] = useState(0);
    const [closeTimeMins, setCloseTimeMins] = useState(0);
    const [description, setDescription] = useState("");
    const [generalSportForms, setGeneralSportForms] = useState<SportForm[]>([]);
    const [isAlwaysPaidBooking, setAlwaysPaidBooking] = useState(false);
    const [isBadWeatherSensitive, setBadWeatherSensitive] = useState(false);
    const [isCommercialOnlyCourt, setIsCommercialOnlyCourt] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [minNumParticipants, setMinNumParticipants] = useState(-1);
    const [name, setName] = useState("");
    const [number, setNumber] = useState(0);
    const [openTimeMins, setOpenTimeMins] = useState(0);
    const [sportForms, setSportForms] = useState<string[]>([]);
    const [surfaceType, setSurfaceType] = useState<typeof surfaceTypes[number]>("clay");
    const [latitude, setLatitude] = useState<string>("");
    const [longitude, setLongitude] = useState<string>("");

    // save action
    const saveAction = async () => {
        setLoading(true);
        try {
            await patchCourts({
                advanceBookingMins,
                bookingPriceCents,
                closeTimeMins,
                courtDescription: description,
                courtIds: [court!.id],
                courtName: name,
                courtNumber: number,
                isAlwaysPaidBooking: isAlwaysPaidBooking || (minNumParticipants > 0),
                isBadWeatherSensitive,
                isCommercialOnlyCourt,
                minNumParticipants,
                openTimeMins,
                sportForms,
                surfaceType,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
            });
            onRefreshCourtsTable(); // fetch update data
            onClose();
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch all sport forms
    const fetchGeneralSportForms = async () => { setGeneralSportForms(await getSportForms()); };
    useEffect(() => { fetchGeneralSportForms(); }, []);

    // update fields
    useEffect(() => {
        if (court) {
            setAdvanceBookingMins(court.advanceBookingMins);
            setAlwaysPaidBooking(court.isAlwaysPaidBooking);
            setBadWeatherSensitive(court.isBadWeatherSensitive);
            setBookingPriceCents(court.bookingPriceCents);
            setCloseTimeMins(court.closeTimeMins);
            setDescription(court.courtDescription);
            setIsCommercialOnlyCourt(court.isCommercialOnlyCourt);
            setMinNumParticipants(court.minNumParticipants);
            setName(court.courtName);
            setNumber(court.courtNumber);
            setOpenTimeMins(court.openTimeMins);
            setSportForms(court.sportForms);
            setSurfaceType(court.surfaceType);
            setLatitude(court.latitude);
            setLongitude(court.longitude);
        }
    }, [court]);

    // render
    const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={show}
            onClose={onClose}
        >
            <DialogTitle>{i18n.editCourt}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>

                    {/* court name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.name}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Grid>

                    {/* court number */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={i18n.number}
                            placeholder="0"
                            type="number"
                            value={number}
                            onBlur={() => (Number.isNaN(number) || number < 0) && setNumber(0)}
                            onChange={(e) => setNumber(parseInt(e.target.value, 10))}
                        />
                    </Grid>

                    {/* booking price */}
                    <Grid item xs={5}>
                        <CurrencyTextField
                            currencySymbol="€"
                            decimalCharacter=","
                            digitGroupSeparator="."
                            disabled={bookingPriceCents === 0}
                            fullWidth
                            label={i18n.price(isAlwaysPaidBooking)}
                            helperText={i18n.priceRange}
                            maximumValue="15"
                            minimumValue={bookingPriceCents === 0 ? "0" : "1"}
                            value={bookingPriceCents / 100}
                            onChange={(e: any, value: number) => setBookingPriceCents(value * 100)}
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <Tooltip title={i18n.priceInfo} arrow>
                            <InfoIcon color="secondary" fontSize="small" style={{ margin: 16 }} />
                        </Tooltip>
                    </Grid>

                    {/* always paid */}
                    <Grid item xs={12}>
                        <FormControl disabled={minNumParticipants > 0}>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={!(isAlwaysPaidBooking || minNumParticipants > 0)}
                                        color="primary"
                                        onChange={(e) => setAlwaysPaidBooking(!e.target.checked)}
                                    />
                                )}
                                label={i18n.freeForClubMembers}
                            />
                        </FormControl>
                    </Grid>

                    {/* booking in advance */}
                    <Grid item xs={12}>
                        <FormControl>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={advanceBookingMins !== -1}
                                        color="primary"
                                        onChange={(e) => setAdvanceBookingMins(e.target.checked ? 2880 : -1)}
                                    />
                                )}
                                label={i18n.advanceBookingMins}
                            />
                        </FormControl>
                        <TextField
                            disabled={advanceBookingMins === -1}
                            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                            placeholder={i18n.unrestricted}
                            type="number"
                            value={(advanceBookingMins === -1) ? "" : advanceBookingMins}
                            onBlur={() => (Number.isNaN(advanceBookingMins) || advanceBookingMins < 0) && setAdvanceBookingMins(-1)}
                            onChange={(e) => setAdvanceBookingMins(parseInt(e.target.value, 10))}
                        />
                    </Grid>

                    {/* min participants */}
                    <Grid item xs={12}>
                        <FormControl>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={minNumParticipants !== -1}
                                        color="primary"
                                        onChange={(e) => setMinNumParticipants(e.target.checked ? 2 : -1)}
                                    />
                                )}
                                label={i18n.minNumParticipants}
                            />
                        </FormControl>
                        <TextField
                            disabled={minNumParticipants === -1}
                            placeholder={i18n.unrestricted}
                            type="number"
                            value={(minNumParticipants === -1) ? "" : minNumParticipants}
                            onBlur={() => (Number.isNaN(minNumParticipants) || minNumParticipants < 2) && setMinNumParticipants(-1)}
                            onChange={(e) => setMinNumParticipants(parseInt(e.target.value, 10))}
                        />
                        <Tooltip title={i18n.minNumParticipantsInfo} arrow>
                            <InfoIcon color="secondary" fontSize="small" style={{ margin: 8 }} />
                        </Tooltip>
                    </Grid>

                    {/* commercial-only court */}
                    {!club?.capabilities.includes("public-ground") && (
                        <Grid item xs={12}>
                            <FormControl disabled={court?.isCommercialOnlyCourt}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={isCommercialOnlyCourt}
                                            color="primary"
                                            onChange={(e) => setIsCommercialOnlyCourt(e.target.checked)}
                                        />
                                    )}
                                    label={i18n.commercialCourt}
                                />
                                {!court?.isCommercialOnlyCourt && isCommercialOnlyCourt && (
                                    <FormHelperText error>{i18n.commercialCourtWarning}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    )}

                    {/* sports form */}
                    <Grid item xs={12}>
                        <Autocomplete
                            filterSelectedOptions
                            getOptionDisabled={(option) => !(generalSportForms.find((f) => f.name === option)?.available)}
                            getOptionLabel={(option) => uI18n.sportForms[option] || capitalize(option)}
                            multiple
                            options={generalSportForms.map((f) => f.name).sort()}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={i18n.sportForm}
                                />
                            )}
                            value={sportForms}
                            onChange={(e, v) => setSportForms(v)}
                        />
                    </Grid>

                    {/* desc */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.description}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>

                    {/* open time */}
                    <Grid item xs={6}>
                        <TimePicker
                            ampm={false}
                            autoOk
                            fullWidth
                            label={i18n.opens}
                            minutesStep={5}
                            value={moment().dayOfYear(1).startOf("day").add(openTimeMins, "minutes") /* dst fix */}
                            variant="inline"
                            onChange={(m) => setOpenTimeMins(m!.diff(m!.clone().startOf("day"), "minutes"))}
                        />
                    </Grid>

                    {/* close time */}
                    <Grid item xs={6}>
                        <TimePicker
                            ampm={false}
                            autoOk
                            fullWidth
                            label={i18n.closes}
                            minutesStep={5}
                            value={moment().dayOfYear(1).startOf("day").add(closeTimeMins, "minutes") /* dst fix */}
                            variant="inline"
                            onChange={(m) => setCloseTimeMins(m!.diff(m!.clone().startOf("day"), "minutes"))}
                        />
                    </Grid>

                    {/* weather sensitive */}
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel id="weather_label">{i18n.weatherDependent}</InputLabel>
                            <Select
                                labelId="weather_label"
                                onChange={(e) => setBadWeatherSensitive(e.target.value === "true")}
                                value={isBadWeatherSensitive ? "true" : "false"}
                            >
                                <MenuItem value="true">{i18n.yesOutdoor}</MenuItem>
                                <MenuItem value="false">{i18n.noIndoor}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* surface type */}
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel id="surface_label">{i18n.surfaceType}</InputLabel>
                            <Select
                                labelId="surface_label"
                                onChange={(e) => setSurfaceType(e.target.value as typeof surfaceType)}
                                value={surfaceType}
                            >
                                {surfaceTypes.map((t) => (
                                    <MenuItem key={t} value={t}>{uI18n.surfaceTypes[t] || capitalize(t)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={i18n.latitude}
                            value={latitude}
                            onChange={(e) => setLatitude(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label={i18n.longitude}
                            value={longitude}
                            onChange={(e) => setLongitude(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            {isLoading && (
                <LinearProgress />
            )}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" onClick={saveAction} disabled={isLoading}>
                    {i18n.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCourtDialog;
